import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  FileText,
  Download,
  Search,
  X,
  CheckCircle2,
  Clock,
  Receipt,
  Link2,
  Unlink,
} from "lucide-react";

// Resolve a backend URL with trailing /api so file downloads can hit
// /sales-services/{id}/invoice and /act through the shared axios instance.
function downloadDocxFromBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

const KIND_LABEL = {
  invoice: "Invoice",
  act: "Act",
};

const KIND_CLASS = {
  invoice: "bg-sky-500/10 text-sky-300 border-sky-500/20",
  act: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
};

export default function FinanceDocumentsPage() {
  const { formatAmount } = useCurrency();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, invoices: 0, acts: 0 });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({}); // service_id+kind → bool

  // Manual payment-linking picker. `picker` holds the row whose payment we're
  // linking; candidates are unmatched bank transactions from the SAME
  // counterparty (matched by name / EDRPOU — backend-enforced).
  const [picker, setPicker] = useState(null); // { row } | null
  const [candidates, setCandidates] = useState([]);
  const [candLoading, setCandLoading] = useState(false);
  const [candMeta, setCandMeta] = useState({}); // { client_name, client_edrpou, reason }
  const [linking, setLinking] = useState(null); // transaction_id in flight

  const today = new Date().toISOString().slice(0, 10);
  const defaultFrom = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  }, []);

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(today);
  const [docType, setDocType] = useState("all");
  const [search, setSearch] = useState("");

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (docType && docType !== "all") params.doc_type = docType;
      if (search.trim()) params.q = search.trim();
      const res = await api.get("/finance/documents", { params });
      setRows(res.data?.items || []);
      setSummary(res.data?.summary || { total: 0, invoices: 0, acts: 0 });
    } catch (err) {
      console.error("Failed to load finance documents", err);
      toast.error(err.response?.data?.detail || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search/filter changes — debounce a touch so typing doesn't fire one
  // request per keystroke.
  useEffect(() => {
    const handle = setTimeout(fetchDocs, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, docType, search]);

  const clearFilters = () => {
    setDateFrom(defaultFrom);
    setDateTo(today);
    setDocType("all");
    setSearch("");
  };

  const openPicker = async (row) => {
    setPicker({ row });
    setCandidates([]);
    setCandMeta({});
    setCandLoading(true);
    try {
      const res = await api.get(`/finance/documents/${row.service_id}/payment-candidates`);
      setCandidates(res.data?.items || []);
      setCandMeta({
        client_name: res.data?.client_name || row.client_name,
        client_edrpou: res.data?.client_edrpou || "",
        reason: res.data?.reason || "",
      });
    } catch (err) {
      console.error("Failed to load payment candidates", err);
      toast.error(err.response?.data?.detail || "Failed to load matching payments");
    } finally {
      setCandLoading(false);
    }
  };

  const linkPayment = async (txId) => {
    if (!picker?.row) return;
    setLinking(txId);
    try {
      await api.post(
        `/sales-services/${picker.row.service_id}/link-transaction`,
        null,
        { params: { transaction_id: txId } },
      );
      toast.success("Payment linked");
      setPicker(null);
      fetchDocs();
    } catch (err) {
      console.error("Link failed", err);
      toast.error(err.response?.data?.detail || "Failed to link payment");
    } finally {
      setLinking(null);
    }
  };

  const unlinkPayment = async (row) => {
    try {
      await api.delete(`/sales-services/${row.service_id}/link-transaction`);
      toast.success("Payment unlinked");
      fetchDocs();
    } catch (err) {
      console.error("Unlink failed", err);
      toast.error(err.response?.data?.detail || "Failed to unlink payment");
    }
  };

  const rowKey = (row) => `${row.source || "sales"}:${row.service_id}:${row.kind}`;

  const downloadDoc = async (row) => {
    const key = rowKey(row);
    setDownloading((s) => ({ ...s, [key]: true }));
    try {
      const docNum = row.doc_number || row.service_id.slice(0, 8);
      // CRM/Support payment docs live on crm_payments and download through the
      // /crm/payments/* endpoints, which return base64 JSON (not a raw blob).
      if (row.source === "crm") {
        const ep = row.kind === "act" ? "download-act" : "download-invoice";
        const res = await api.get(`/crm/payments/${row.service_id}/${ep}`, {
          params: { format: "docx" },
        });
        const { doc_base64, pdf_base64, format: fmt } = res.data || {};
        const data = doc_base64 || pdf_base64;
        const isDocx = fmt ? fmt === "docx" : true;
        const mime = isDocx
          ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          : "application/pdf";
        const ext = isDocx ? "docx" : "pdf";
        const blob = new Blob([Uint8Array.from(atob(data), (c) => c.charCodeAt(0))], { type: mime });
        downloadDocxFromBlob(blob, `${row.kind}-${docNum}.${ext}`);
        return;
      }
      const path = row.kind === "act"
        ? `/sales-services/${row.service_id}/act`
        : `/sales-services/${row.service_id}/invoice`;
      // Backend streams a raw DOCX/PDF body with Content-Disposition. Pull as
      // a blob and grab the filename from the response header.
      const res = await api.get(path, {
        params: { format: "docx" },
        responseType: "blob",
      });
      const blob = res.data;
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      const filename = (match && decodeURIComponent(match[1]))
        || `${row.kind}-${docNum}.docx`;
      downloadDocxFromBlob(blob, filename);
    } catch (err) {
      console.error("Download failed", err);
      // Error body comes back as a Blob (responseType=blob) — try to read it
      // as text and surface the JSON detail message if present.
      let detail = "Download failed";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          detail = parsed.detail || detail;
        } catch (_) { /* keep default */ }
      } else if (err.response?.data?.detail) {
        detail = err.response.data.detail;
      }
      toast.error(detail);
    } finally {
      setDownloading((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <div data-testid="finance-documents-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Invoices & Acts</h1>
          <p className="text-sm text-zinc-500">
            Every invoice and act issued across projects, support, and CRM —
            searchable so counterparty enquiries become one click instead of a hunt.
          </p>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Documents</p>
              <p className="text-2xl font-bold text-zinc-50">{summary.total}</p>
            </div>
            <FileText className="h-8 w-8 text-zinc-700" />
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Invoices</p>
              <p className="text-2xl font-bold text-sky-300">{summary.invoices}</p>
            </div>
            <Receipt className="h-8 w-8 text-sky-900" />
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Acts</p>
              <p className="text-2xl font-bold text-emerald-300">{summary.acts}</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-emerald-900" />
          </CardContent>
        </Card>
      </div>

      {/* Filter bar */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-500">From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-500">To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-500">Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all" className="text-zinc-50">All</SelectItem>
                  <SelectItem value="invoice" className="text-zinc-50">Invoices only</SelectItem>
                  <SelectItem value="act" className="text-zinc-50">Acts only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 lg:col-span-1">
              <Label className="text-xs text-zinc-500">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Document #, client, project…"
                  className="bg-zinc-800 border-zinc-700 text-zinc-50 pl-8"
                />
              </div>
            </div>
            <div>
              <Button
                variant="outline"
                onClick={clearFilters}
                className="border-zinc-700 text-zinc-300 w-full"
              >
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-orange-500" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-zinc-500 space-y-2">
              <FileText className="h-8 w-8 mx-auto text-zinc-700" />
              <p>No documents match the current filters.</p>
              <p className="text-xs text-zinc-600">
                Adjust the date range, switch the type filter, or clear filters to see everything issued so far.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 font-medium">Date</TableHead>
                  <TableHead className="text-zinc-400 font-medium">Type</TableHead>
                  <TableHead className="text-zinc-400 font-medium">Document #</TableHead>
                  <TableHead className="text-zinc-400 font-medium">Counterparty</TableHead>
                  <TableHead className="text-zinc-400 font-medium">Project</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right">Amount</TableHead>
                  <TableHead className="text-zinc-400 font-medium">Payment</TableHead>
                  <TableHead className="text-zinc-400 font-medium text-right w-32"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const key = rowKey(row);
                  const isDl = !!downloading[key];
                  return (
                    <TableRow key={key} className="border-zinc-800 hover:bg-zinc-800/40">
                      <TableCell className="text-zinc-300 font-mono text-xs whitespace-nowrap">
                        {row.effective_date || "—"}
                        {row.sent_at && row.sent_at !== row.doc_date && (
                          <span className="block text-[10px] text-zinc-600">
                            sent {row.sent_at.slice(0, 10)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={KIND_CLASS[row.kind]}>
                          {KIND_LABEL[row.kind]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-zinc-200 font-mono text-xs">
                        {row.doc_number || <span className="text-zinc-600">—</span>}
                      </TableCell>
                      <TableCell className="text-zinc-50">
                        <div className="text-sm">{row.client_name || "—"}</div>
                        {row.client_legal_name && row.client_legal_name !== row.client_name && (
                          <div className="text-[11px] text-zinc-500">{row.client_legal_name}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-300 text-sm">
                        {row.project_name || "—"}
                        {row.service_name && (
                          <div className="text-[11px] text-zinc-500">{row.service_name}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-zinc-200 font-mono whitespace-nowrap">
                        {row.amount
                          ? formatAmount(row.amount, row.currency)
                          : <span className="text-zinc-600">—</span>}
                        <div className="text-[11px] text-zinc-500">{row.currency}</div>
                      </TableCell>
                      <TableCell>
                        {row.source === "crm" ? (
                          /* CRM/Support payment docs reconcile on /crm-income,
                             not here — show read-only paid state, no sales-side
                             linking picker (its endpoints are sales_services only). */
                          row.is_paid ? (
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                              <span className="text-xs text-zinc-300">
                                Paid{row.paid_date ? ` · ${row.paid_date.slice(0, 10)}` : ""}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-zinc-600 shrink-0" />
                              <span className="text-xs text-zinc-500">Not paid</span>
                            </div>
                          )
                        ) : row.payment ? (
                          /* State 1 — a real bank transaction is linked: show
                             exactly how it was paid (payer, EDRPOU, date, sum). */
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <div
                                className="text-xs text-zinc-200 truncate max-w-[200px]"
                                title={row.payment.payer_name || ""}
                              >
                                {row.payment.payer_name || "—"}
                              </div>
                              <div className="text-[10px] text-zinc-500">
                                {row.payment.payer_edrpou ? `EDRPOU ${row.payment.payer_edrpou} · ` : ""}
                                {(row.payment.date || "").slice(0, 10)}
                                {row.payment.amount
                                  ? ` · ${formatAmount(row.payment.amount, row.payment.currency)} ${row.payment.currency}`
                                  : ""}
                              </div>
                              <button
                                type="button"
                                onClick={() => unlinkPayment(row)}
                                className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] text-zinc-600 hover:text-red-400"
                              >
                                <Unlink className="h-3 w-3" /> unlink
                              </button>
                            </div>
                          </div>
                        ) : row.is_paid ? (
                          /* State 2 — marked paid manually, no bank transaction
                             attached. It IS paid (show by whom / when); linking a
                             bank payment is optional reconciliation, kept quiet. */
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <div className="text-xs text-zinc-300">
                                Paid{row.paid_date ? ` · ${row.paid_date.slice(0, 10)}` : ""}
                              </div>
                              <div className="text-[10px] text-zinc-600">
                                {row.paid_by_name ? `marked by ${row.paid_by_name}` : "no bank payment linked"}
                              </div>
                              <button
                                type="button"
                                onClick={() => openPicker(row)}
                                className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] text-zinc-600 hover:text-orange-400"
                                title="Attach the matching bank transaction (reconcile)"
                              >
                                <Link2 className="h-3 w-3" /> link bank payment
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* State 3 — unpaid: prominent link action. */
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-zinc-600 shrink-0" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openPicker(row)}
                              className="h-7 border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs"
                              title="Link a bank payment from this counterparty"
                            >
                              <Link2 className="h-3 w-3 mr-1" /> Link payment
                            </Button>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadDoc(row)}
                          disabled={isDl}
                          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                          title={`Download ${KIND_LABEL[row.kind]} DOCX`}
                        >
                          <Download className="h-3.5 w-3.5 mr-1" />
                          {isDl ? "…" : "DOCX"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Manual payment-linking picker — only payers matching this document's
          counterparty (by name / EDRPOU) are offered, enforced server-side. */}
      <Dialog open={!!picker} onOpenChange={(open) => { if (!open) setPicker(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Link a payment</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {picker?.row && (
                <>
                  Matching incoming payments for{" "}
                  <span className="text-zinc-300">{candMeta.client_name || picker.row.client_name}</span>
                  {candMeta.client_edrpou ? <> · EDRPOU {candMeta.client_edrpou}</> : null}
                  {picker.row.doc_number ? <> · doc {picker.row.doc_number}</> : null}.
                  {" "}Only payers matching this counterparty by name or EDRPOU are shown.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
            {candLoading ? (
              <div className="flex items-center justify-center h-24">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-orange-500" />
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-center py-10 text-sm text-zinc-500 space-y-1">
                <p>
                  {candMeta.reason === "no_client"
                    ? "This document isn't linked to a client, so payments can't be matched by counterparty."
                    : candMeta.reason === "no_identity"
                    ? "This client has no EDRPOU or legal name on file to match payments against."
                    : "No unmatched payments from this counterparty were found."}
                </p>
                {!candMeta.reason && (
                  <p className="text-xs text-zinc-600">
                    Either the payment hasn't arrived yet, or it's already linked to another document.
                  </p>
                )}
              </div>
            ) : (
              <div className="divide-y divide-zinc-800">
                {candidates.map((c) => (
                  <div key={c.transaction_id} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-200 truncate" title={c.payer_name || ""}>
                        {c.payer_name || "—"}
                      </div>
                      <div className="text-[11px] text-zinc-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span>{(c.date || "").slice(0, 10)}</span>
                        <span className="text-zinc-300">
                          {formatAmount(c.amount, c.currency)} {c.currency}
                        </span>
                        {c.payer_edrpou && <span>EDRPOU {c.payer_edrpou}</span>}
                        <Badge
                          variant="outline"
                          className={
                            c.match_reason === "edrpou"
                              ? "border-emerald-500/30 text-emerald-300"
                              : "border-amber-500/30 text-amber-300"
                          }
                        >
                          {c.match_reason === "edrpou" ? "EDRPOU match" : "name match"}
                        </Badge>
                      </div>
                      {c.purpose && (
                        <div className="text-[10px] text-zinc-600 truncate" title={c.purpose}>
                          {c.purpose}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => linkPayment(c.transaction_id)}
                      disabled={!!linking}
                      className="bg-orange-600 hover:bg-orange-700 text-white shrink-0"
                    >
                      {linking === c.transaction_id ? "…" : "Link"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
